import React from 'react';
import { Link } from 'react-router-dom';
import styles from './OpsAssist.module.css';
import Footer from '../shared/Footer';

// ===== DATA CONSTANTS =====
// Core features of OpsAssist product
const FEATURES = [
  {
    icon: '🤖',
    title: 'AI-based Task Assignment',
    description: 'Assign and reassign driver tasks in real-time for maximum efficiency.'
  },
  {
    icon: '📊',
    title: 'Resource Management',
    description: 'Match drivers to tasks by location and capacity, with real-time updates to their devices.'
  },
  {
    icon: '🚨',
    title: 'Proactive Alerts',
    description: 'Stay ahead with alerts that prevent delays and ensure smooth operations.'
  },
  {
    icon: '📈',
    title: 'Live Dashboard',
    description: 'Get real-time visibility into ground operations performance and status.'
  },
  {
    icon: '🔗',
    title: 'Efficient Task Chaining',
    description: 'Smart task sequencing to save time and enhance productivity.'
  },
  {
    icon: '🚀',
    title: 'Future-Ready Hybrid Ops',
    description: 'Easily integrate with existing systems while staying ready for future operations.'
  }
];

// Performance metrics and impact statistics
const IMPACT_STATS = [
  { number: '20%', label: 'Less Work Hours Per Flight' },
  { number: '22%', label: 'More Trips Per Driver' },
  { number: '100%', label: 'Timely Bag Delivery' }
];

// Customer testimonials and quotes
const TESTIMONIALS = [
  {
    quote: "The ease of adoption was remarkable. Our team was up and running with OpsAssist in no time, and the results speak for themselves.",
    author: "Ranjiv Ramanathan",
    role: "Senior Vice President, SG Hub Transformation and Development at SATS Ltd.",
    icon: "Ranjiv-Ramanathan.jfif"
  },
  {
    quote: "Precision timing resource allocation was impossible to do it in the past and to do this manually. We now have the tools to be able to analyse real-time data, we can then sequence and then direct resource allocation. Thank you Neural Lab for working with us to get this to scale.",
    author: "Henry Low",
    role: "Chief Executive Officer, SATS Singapore Hub",
    icon: "Henry-Low.jfif"
  },
  {
    quote: "OpsAssist represents the future of ground operations. It's exactly what the industry needs to address current challenges.",
    author: "Claire Donnellan",
    role: "Director of Customer Centric Consulting",
    icon: "Claire-Donnellan.jfif"
  }
];

// ===== MAIN COMPONENT =====
function OpsAssist() {
  return (
    <div className={styles.section}>
      <div className={styles.container}>
        {/* Hero Section with Product Overview */}
        <div className={styles.hero}>
          <div className={styles.heroContent}>
            <div className={styles.logoContainer}>
              <img 
                src={`${process.env.PUBLIC_URL}/images/OpsAssist.png`}
                alt="OpsAssist"
                className={styles.opsAssistLogo}
              />
              <span className={styles.badge}>Trusted by SATS</span>
            </div>
            <p className={styles.subtitle}>
              Transforming airport ground operations with intelligent, real-time decisions powered by AI.
            </p>
            <p className={styles.subheading}>
              Agentic AI, Dynamic Day-of-Ops & Built to Scale with You
            </p>
            <p className={styles.description}>
              OpsAssist is an AI-powered ground operations assistant that automates task assignment, optimizes resources, and delivers real-time operational insights—proven to boost efficiency and ROI in just six months at Changi Airport.
            </p>
            <div className={styles.heroButtons}>
              <Link to="/#contact" className={styles.btnPrimary}>
                Request a Demo
              </Link>
            </div>
          </div>
          <div className={styles.heroVisual}>
            <img 
              src={`${process.env.PUBLIC_URL}/images/ops-assist/mobile-desktop-mockup.png`}
              alt="OpsAssist Mobile and Desktop Interface"
              className={styles.mockupImage}
            />
          </div>
        </div>

        {/* Why OpsAssist Section */} {/* Updated: Fixed Ops Assist to OpsAssist */}
        <div className={styles.problemSection}>
          <div className={styles.container}>
            <h2 className={styles.sectionTitle}>Why OpsAssist</h2> {/* Updated: Fixed Ops Assist to OpsAssist */}
            <p className={styles.sectionSubtitle}>Result Proven, Real Impact</p>
            
            <div className={styles.metricsGrid}>
              <div className={styles.metricCard}>
                <div className={styles.metricNumber}>100%</div>
                <div className={styles.metricLabel}>Service Standard Achievement</div>
              </div>
              <div className={styles.metricCard}>
                <div className={styles.metricNumber}>20%+</div>
                <div className={styles.metricLabel}>Operational Capacity in Dynamic Environment</div>
              </div>
              <div className={styles.metricCard}>
                <div className={styles.metricNumber}>2x</div>
                <div className={styles.metricLabel}>More Profit Margin</div>
              </div>
            </div>

            <div className={styles.operationalAdvantages}>
              <h3 className={styles.advantagesTitle}>Operational Advantages</h3>
              <div className={styles.advantagesGrid}>
                <div className={styles.advantageCard}>
                  <div className={styles.advantageIcon}>✅</div>
                  <h4>Resources line of sight</h4>
                  <p>Clear visual of location, task progress, and availability</p>
                </div>
                <div className={styles.advantageCard}>
                  <div className={styles.advantageIcon}>✅</div>
                  <h4>Dynamic Day-of-Ops</h4>
                  <p>Auto-adapt to flight or task changes to enable seamless responsive operations.</p>
                </div>
                <div className={styles.advantageCard}>
                  <div className={styles.advantageIcon}>✅</div>
                  <h4>Real-Time Resilience</h4>
                  <p>Dynamic updates and smart reassignment eliminate manual replanning pressure.</p>
                </div>
                <div className={styles.advantageCard}>
                  <div className={styles.advantageIcon}>✅</div>
                  <h4>Smart Communications</h4>
                  <p>Automated task updates and real-time alerts replace manual back-and-forth</p>
                </div>
              </div>
            </div>
          </div>
        </div>


        {/* Key Features */}
        <div className={styles.featuresSection}>
          <h2 className={styles.sectionTitle}>Key Features</h2>
          <div className={styles.featuresGrid}>
            {FEATURES.map((feature, index) => (
              <div key={index} className={styles.featureCard}>
                <div className={styles.featureIcon}>{feature.icon}</div>
                <h3 className={styles.featureTitle}>{feature.title}</h3>
                <p className={styles.featureDescription}>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Future AI Airport */}
        <div className={styles.futureSection}>
          <div className={styles.container}>
            <h2 className={styles.sectionTitle}>OpsAssist in Action</h2> {/* Updated: Fixed Ops Assist to OpsAssist */}
            <div className={styles.videosGrid}>
              <div className={styles.videoCard}>
                <video 
                  className={styles.videoPlayer}
                  autoPlay
                  loop
                  muted
                  playsInline
                >
                  <source 
                    src={process.env.PUBLIC_URL + '/images/ops-assist/demo.mp4'} 
                    type="video/mp4"
                  />
                  Your browser does not support the video tag.
                </video>
                <div className={styles.videoTitle}>
                  OpsAssist Demo
                  <br />
                  <small>See the system in action</small>
                </div>
              </div>
              <div className={styles.videoCard}>
                <video 
                  className={styles.videoPlayer}
                  autoPlay
                  loop
                  muted
                  playsInline
                >
                  <source 
                    src={process.env.PUBLIC_URL + '/images/ops-assist/task-based.mp4'} 
                    type="video/mp4"
                  />
                  Your browser does not support the video tag.
                </video>
                <div className={styles.videoTitle}>
                  Resource-Optimized Operations
                  <br />
                  <small>Intelligent task management in real-time</small>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Testimonials */}
        <div className={styles.testimonialsSection}>
          <h2 className={styles.sectionTitle}>Industry Endorsement</h2>
          <div className={styles.testimonialsGrid}>
            {TESTIMONIALS.map((testimonial, index) => (
              <div key={index} className={styles.testimonialCard}>
                <p className={styles.testimonialQuote}>"{testimonial.quote}"</p>
                <div className={styles.testimonialAuthor}>
                  {testimonial.icon && (
                    <img 
                      src={`${process.env.PUBLIC_URL}/images/icon/${testimonial.icon}`}
                      alt={testimonial.author}
                      className={styles.authorIcon}
                    />
                  )}
                  <div className={styles.authorInfo}>
                    <div className={styles.authorName}>{testimonial.author}</div>
                    <div className={styles.authorRole}>{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Videos Section */}
        <div className={styles.videosSection}>
          <h2 className={styles.sectionTitle}>How OpsAssist Is Changing the Game</h2> {/* Updated: Fixed Ops Assist to OpsAssist */}
          <div className={styles.videosGridSmall}>
            <div className={styles.videoCard}>
              <div className={styles.videoEmbed}>
                <iframe
                  width="100%"
                  height="315"
                  src="https://www.youtube.com/embed/WlGV50wkF8k"
                  title="Ranjiv Ramanathan Interview"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
                <div className={styles.videoTitle}>
                  Ranjiv Ramanathan Interview
                  <br />
                  <small>Operations Manager discussing OpsAssist adoption</small>
                </div>
              </div>
            </div>
            {/* <div className={styles.videoCard}>
              <div className={styles.videoEmbed}>
                <iframe
                  width="100%"
                  height="315"
                  src="https://www.youtube.com/embed/7gOaNonoiOY"
                  title="Henry Low Testimonial"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
                <div className={styles.videoTitle}>
                  Henry Low Testimonial
                  <br />
                  <small>CEO discussing OpsAssist impact at SATS Singapore Hub</small>
                </div>
              </div>
            </div> */}
          </div>
        </div>

        {/* Call to Action */}
        <div className={styles.ctaSection}>
          <h2 className={styles.ctaTitle}>Start your OpsAssist journey today</h2>
          <p className={styles.ctaText}>
            Streamline ground operations with AI-powered precision. Transform airport operations with intelligent automation. Trusted by the world's leading airports.
          </p>
          <div className={styles.ctaButtons}>
            <Link to="/#contact" className={styles.btnSecondary}>
              Contact Us
            </Link>
            <Link to="/#contact" className={styles.btnPrimary}>
              Request a Demo
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default OpsAssist;
